import React, {forwardRef, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import {useIntl} from 'react-intl'
import {
    Box,
    Button,
    Checkbox as ChakraCheckbox,
    Field as ChakraField,
    IconButton,
    Input as ChakraInput,
    InputGroup,
    useSlotRecipe
} from '@chakra-ui/react3'
import {CalendarIcon, CheckboxIcon, CheckIcon, EyeIcon, WarningIcon} from '../icons'
import {getDateRange, isDateInRange, parseBackendDate} from '../../../utils/date-utils'
import inputRecipe from './index.recipe'
import {useController} from 'react-hook-form'
import {useMergeRefs} from '@chakra-ui/react-use-merge-refs'

const BaseDatePicker = loadable(() => import('../../../components/date-picker'), {ssr: false})

/**
 * Input rendered by the date picker for `type="date"`: the Leap input inside an input group.
 * react-datepicker passes its own input ref as `inputRef` and its handlers as props.
 */
const DateInput = forwardRef(function DateInput(
    {inputRef, endElement, endElementProps, groupCss, ...props},
    ref
) {
    const combinedRef = useMergeRefs(ref, inputRef)

    return (
        <InputGroup endElement={endElement} endElementProps={endElementProps} css={groupCss}>
            <ChakraInput ref={combinedRef} {...props} />
        </InputGroup>
    )
})

DateInput.propTypes = {
    inputRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    endElement: PropTypes.node,
    endElementProps: PropTypes.object,
    groupCss: PropTypes.object
}

/**
 * Leap input field built on top of Chakra Field and Input.
 *
 * Supports labels, helper and error text, optional and required markers,
 * start/end tokens, password visibility toggle, clearable search behavior,
 * success or warning status icons, and a date picker for `type="date"`.
 *
 * @param {Object} props
 * @param {string} [props.id] - Explicit input id. Falls back to a generated id.
 * @param {React.ReactNode} [props.label] - Accessible field label.
 * @param {boolean} [props.hideLabel=false] - Visually hides the label while keeping it accessible.
 * @param {'text'|'email'|'password'|'search'|'tel'|'number'|'checkbox'|'date'} [props.type='text'] - Native input type;
 * `date` renders the date picker and stores an ISO date string (`YYYY-MM-DD`).
 * @param {React.ReactNode} [props.helperText] - Helper text linked through `aria-describedby`.
 * @param {React.ReactNode} [props.errorText] - Error text linked through `aria-describedby`.
 * @param {Object} [props.error] - react-hook-form field error, as provided by the field definitions of
 * `useGlobalFields`; its `message` is shown when no `errorText` is given.
 * @param {React.ReactNode} [props.optionalText] - Optional text shown next to the label when field is not required.
 * @param {'outline'|'filled'|'subtle'} [props.variant='outline'] - Visual input variant; checkboxes support `outline` and `filled`.
 * @param {boolean} [props.required=false] - Marks the field as required.
 * @param {boolean} [props.isRequired=false] - Legacy alias for `required`.
 * @param {boolean} [props.disabled=false] - Disables the field.
 * @param {boolean} [props.isDisabled=false] - Legacy alias for `disabled`.
 * @param {boolean} [props.invalid=false] - Forces invalid state even without `errorText`.
 * @param {boolean} [props.isInvalid=false] - Legacy alias for `invalid`.
 * @param {string} [props.placeholder] - Native input placeholder.
 * @param {React.ReactNode} [props.startElement] - Content rendered in the start slot.
 * @param {React.ReactNode|Function} [props.endElement] - Content rendered in the end slot. Overrides built-in status and action elements.
 * @param {Object} [props.startElementProps] - Additional props for the start slot wrapper.
 * @param {Object} [props.endElementProps] - Additional props for the end slot wrapper.
 * @param {string} [props.startOffset] - Space deducted from the automatic input start padding.
 * @param {boolean} [props.clearable=false] - Enables clear action for search input with a current value.
 * @param {Function} [props.onClear] - Callback fired when clearable search input is cleared.
 * @param {Function} [props.onCheckedChange] - Chakra checkbox change handler for `type="checkbox"`.
 * @param {'success'|'warning'} [props.status] - Non-error validation status.
 * @param {string} [props.statusLabel] - Accessible label for status icons.
 * @param {Function} [props.onChange] - Change handler for input or checkbox-like event payload.
 * @param {string|number|boolean} [props.defaultValue] - Initial field value.
 * @param {Date} [props.minDate] - Earliest selectable date for `type="date"`. Defaults to 100 years ago.
 * @param {Date} [props.maxDate] - Latest selectable date for `type="date"`. Defaults to today.
 * Typed dates outside `minDate` .. `maxDate` fail validation.
 * @param {Object} [props.inputProps] - Additional props for the native input (`Checkbox.HiddenInput` when `type="checkbox"`), e.g. `autoComplete`.
 * @param {React.Ref<HTMLInputElement>} ref - Forwarded input ref.
 * @returns {React.ReactElement}
 */
export const InputField = forwardRef(function InputField(
    {
        id,
        name,
        control,
        rules = {},
        label,
        hideLabel = false,
        type = 'text',
        helperText,
        errorText,
        error,
        optionalText,
        variant = 'outline',
        required = false,
        isRequired = false,
        disabled = false,
        isDisabled = false,
        invalid = false,
        isInvalid = false,
        placeholder,
        startElement,
        endElement,
        startElementProps,
        endElementProps,
        startOffset,
        clearable = false,
        onClear,
        onCheckedChange,
        status,
        statusLabel,
        onChange,
        defaultValue,
        minDate,
        maxDate,
        inputProps,
        ...nativeInputProps
    },
    ref
) {
    const {formatMessage, formatDate} = useIntl()
    const [isPasswordVisible, setPasswordVisible] = useState(false)
    const isCheckbox = type === 'checkbox'
    const isDate = type === 'date'
    const isPassword = type === 'password'
    const resolvedRequired = required || isRequired
    const resolvedDisabled = disabled || isDisabled
    const resolvedInvalid = invalid || isInvalid
    const resolvedType = isPassword && isPasswordVisible ? 'text' : type
    const recipe = useSlotRecipe({recipe: inputRecipe})

    // the calendar only blocks days outside the range; typed dates are rejected here
    const getDateRules = () => {
        const range = getDateRange(minDate, maxDate)
        const validate =
            typeof rules.validate === 'function' ? {custom: rules.validate} : rules.validate

        return {
            ...rules,
            validate: {
                ...validate,
                dateRange: (dateValue) => {
                    const date = parseBackendDate(dateValue)

                    return (
                        !date ||
                        isDateInRange(date, range.minDate, range.maxDate) ||
                        formatMessage(
                            {
                                id: 'leap.input_field.error.date_out_of_range',
                                defaultMessage:
                                    'Please enter a date between {minDate} and {maxDate}.'
                            },
                            {minDate: formatDate(range.minDate), maxDate: formatDate(range.maxDate)}
                        )
                    )
                }
            }
        }
    }

    const {
        field: {
            ref: fieldRef,
            value,
            onChange: fieldOnChange,
            onBlur: fieldOnBlur,
            name: fieldName
        },
        fieldState
    } = useController({
        name,
        control,
        rules: isDate ? getDateRules() : rules,
        defaultValue: isCheckbox ? Boolean(defaultValue) : defaultValue,
        disabled: resolvedDisabled
    })
    const combinedRef = useMergeRefs(fieldRef, ref)
    const resolvedErrorText = errorText ?? error?.message ?? fieldState.error?.message
    const hasError = resolvedInvalid || fieldState.invalid || Boolean(resolvedErrorText)
    const styles = useMemo(() => recipe({disabled: resolvedDisabled}), [recipe, resolvedDisabled])
    const datePickerInputProps = {
        ...nativeInputProps,
        ...inputProps,
        variant,
        disabled: resolvedDisabled,
        groupCss: styles.group,
        endElement: <CalendarIcon aria-hidden {...styles.icon} />,
        endElementProps: {...styles.endElement, ...styles.pickerEndElement}
    }
    const invalidInputLabel =
        statusLabel ||
        formatMessage({
            id: 'global.invalid_input',
            defaultMessage: 'Invalid input'
        })
    const validInputLabel =
        statusLabel ||
        formatMessage({
            id: 'global.valid_input',
            defaultMessage: 'Valid input'
        })
    const warningLabel =
        statusLabel ||
        formatMessage({
            id: 'global.warning',
            defaultMessage: 'Warning'
        })

    const handleValueChange = (nextValue, event) => {
        fieldOnChange(nextValue)

        onChange?.(event)
    }

    const handleCheckedChange = (details) => {
        const nextChecked = details.checked === true

        fieldOnChange(nextChecked)
        onCheckedChange?.(details)
        onChange?.({
            target: {
                checked: nextChecked,
                id,
                name: fieldName,
                value: nativeInputProps.value
            }
        })
    }

    const renderEndElement = () => {
        if (typeof endElement === 'function') {
            return endElement({value: value ?? '', invalid: hasError, disabled})
        }

        if (endElement) return endElement

        if (hasError) {
            return (
                <InputToken srLabel={invalidInputLabel}>
                    <WarningIcon {...styles.icon} {...styles.errorIcon} />
                </InputToken>
            )
        }

        if (clearable && type === 'search' && value) {
            return (
                <Button
                    {...styles.deleteButton}
                    aria-label={formatMessage({
                        defaultMessage: 'Delete input',
                        id: 'product_list.search.delete_input'
                    })}
                    onClick={() => {
                        fieldOnChange('')
                        onClear?.()
                    }}
                >
                    {formatMessage({
                        defaultMessage: 'Delete',
                        id: 'global.delete'
                    })}
                </Button>
            )
        }

        if (isPassword) {
            return (
                <IconButton
                    {...styles.iconButton}
                    aria-label={
                        isPasswordVisible
                            ? formatMessage({
                                  id: 'global.hide_password',
                                  defaultMessage: 'Hide password'
                              })
                            : formatMessage({
                                  id: 'global.show_password',
                                  defaultMessage: 'Show password'
                              })
                    }
                    aria-pressed={isPasswordVisible}
                    onClick={() => setPasswordVisible((visible) => !visible)}
                >
                    {isPasswordVisible ? (
                        <EyeIcon {...styles.icon} />
                    ) : (
                        <EyeIcon {...styles.icon} />
                    )}
                </IconButton>
            )
        }

        if (status === 'success') {
            return (
                <InputToken srLabel={validInputLabel}>
                    <CheckIcon {...styles.icon} />
                </InputToken>
            )
        }

        if (status === 'warning') {
            return (
                <InputToken srLabel={warningLabel}>
                    <WarningIcon {...styles.icon} />
                </InputToken>
            )
        }

        return null
    }

    return (
        <ChakraField.Root
            id={id}
            required={resolvedRequired}
            invalid={hasError}
            disabled={resolvedDisabled}
            variant={type === 'checkbox' ? 'checkbox' : undefined}
        >
            {type !== 'checkbox' && label ? (
                <ChakraField.Label className={hideLabel ? 'sr-only' : undefined}>
                    {label}
                    <ChakraField.RequiredIndicator />
                    {!resolvedRequired && optionalText ? (
                        <Box as="span" marginInlineStart="spacing-4">
                            {optionalText}
                        </Box>
                    ) : null}
                </ChakraField.Label>
            ) : null}
            {type === 'checkbox' && (
                <ChakraCheckbox.Root
                    {...nativeInputProps}
                    name={fieldName}
                    checked={Boolean(value)}
                    disabled={resolvedDisabled}
                    invalid={hasError}
                    required={resolvedRequired}
                    onCheckedChange={handleCheckedChange}
                    variant={variant}
                >
                    <ChakraCheckbox.HiddenInput {...inputProps} ref={combinedRef} />
                    <ChakraCheckbox.Control>
                        <ChakraCheckbox.Indicator checked={<CheckboxIcon aria-hidden />} />
                    </ChakraCheckbox.Control>
                    {label ? (
                        <ChakraCheckbox.Label className={hideLabel ? 'sr-only' : undefined}>
                            {label}
                            {!resolvedRequired && optionalText ? (
                                <Box as="span" marginInlineStart="spacing-4">
                                    {optionalText}
                                </Box>
                            ) : null}
                        </ChakraCheckbox.Label>
                    ) : null}
                </ChakraCheckbox.Root>
            )}
            {isDate && (
                <BaseDatePicker
                    ref={combinedRef}
                    inputComponent={DateInput}
                    inputProps={datePickerInputProps}
                    fallback={
                        <DateInput
                            ref={combinedRef}
                            {...datePickerInputProps}
                            readOnly
                            placeholder={placeholder}
                        />
                    }
                    placeholder={placeholder}
                    value={value}
                    minDate={minDate}
                    maxDate={maxDate}
                    keepOutOfRangeInput
                    onBlur={(event) => {
                        fieldOnBlur()
                        nativeInputProps.onBlur?.(event)
                    }}
                    onChange={fieldOnChange}
                />
            )}
            {!isCheckbox && !isDate && (
                <InputGroup
                    startElement={startElement}
                    startOffset={startOffset}
                    endElement={renderEndElement()}
                    startElementProps={{
                        ...styles.startElement,
                        ...startElementProps
                    }}
                    endElementProps={{
                        ...styles.endElement,
                        pointerEvents: endElement ? 'auto' : undefined,
                        ...endElementProps
                    }}
                    css={styles.group}
                >
                    <ChakraInput
                        {...nativeInputProps}
                        {...inputProps}
                        ref={combinedRef}
                        type={resolvedType}
                        placeholder={placeholder}
                        variant={variant}
                        value={value ?? ''}
                        disabled={resolvedDisabled}
                        onChange={(event) => handleValueChange(event.target.value, event)}
                        // Suppress the focus-visible ring when the user clicks with a mouse.
                        // Browsers show :focus-visible on click for inputs (unlike buttons), so we
                        // track mouse intent via a data attribute and strip the outline only then —
                        // keyboard users still get the full focus ring.
                        onMouseDown={(e) => {
                            e.currentTarget.setAttribute('data-mouse-focused', 'true')
                            nativeInputProps.onMouseDown?.(e)
                        }}
                        onBlur={(e) => {
                            e.currentTarget.removeAttribute('data-mouse-focused')
                            nativeInputProps.onBlur?.(e)
                        }}
                        onKeyDown={(e) => {
                            e.currentTarget.removeAttribute('data-mouse-focused')
                            nativeInputProps.onKeyDown?.(e)
                        }}
                    />
                </InputGroup>
            )}
            {resolvedErrorText && hasError ? (
                <ChakraField.ErrorText>{resolvedErrorText}</ChakraField.ErrorText>
            ) : null}
            {helperText ? <ChakraField.HelperText>{helperText}</ChakraField.HelperText> : null}
        </ChakraField.Root>
    )
})

InputField.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    control: PropTypes.object,
    rules: PropTypes.object,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    hideLabel: PropTypes.bool,
    type: PropTypes.oneOf([
        'text',
        'email',
        'password',
        'search',
        'tel',
        'number',
        'checkbox',
        'date'
    ]),
    helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    errorText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    error: PropTypes.shape({message: PropTypes.string}),
    optionalText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    variant: PropTypes.oneOf(['outline', 'filled', 'subtle']),
    required: PropTypes.bool,
    isRequired: PropTypes.bool,
    disabled: PropTypes.bool,
    isDisabled: PropTypes.bool,
    invalid: PropTypes.bool,
    isInvalid: PropTypes.bool,
    placeholder: PropTypes.string,
    startElement: PropTypes.node,
    endElement: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    startElementProps: PropTypes.object,
    endElementProps: PropTypes.object,
    startOffset: PropTypes.string,
    clearable: PropTypes.bool,
    onClear: PropTypes.func,
    onCheckedChange: PropTypes.func,
    status: PropTypes.oneOf(['success', 'warning']),
    statusLabel: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    onChange: PropTypes.func,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    inputProps: PropTypes.object
}

export const InputToken = ({children, srLabel = ''}) => {
    const recipe = useSlotRecipe({recipe: inputRecipe})
    const styles = recipe()

    if (!children) return null

    const content = React.isValidElement(children)
        ? React.cloneElement(children, {
              ...styles.icon,
              ...children.props,
              'aria-hidden': srLabel ? true : children.props['aria-hidden']
          })
        : children

    return (
        <Box display="inline-flex" alignItems="center" justifyContent="center">
            {srLabel ? <Box className="sr-only">{srLabel}</Box> : null}
            <Box as="span">{content}</Box>
        </Box>
    )
}

InputToken.propTypes = {
    children: PropTypes.node,
    srLabel: PropTypes.string
}

export default InputField
